import type { ConversationMessage, VickiMission, VickiWaitingOn, VickiWatch } from '@deskworks/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ChevronDown, Eye, Hourglass, Loader2, Square, Target, X } from 'lucide-react';
import { useState } from 'react';
import { api } from '@/lib/api';
import { formatAbsolute } from '@/lib/format';
import { useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { useRealtimeEnvelope } from '@/lib/realtime';
import { cn } from '@/lib/utils';

const WAITING: Record<VickiWaitingOn, { label: string; className: string }> = {
  vicki: { label: t('VICKI is on it'), className: 'text-indigo-700 dark:text-indigo-300' },
  desks: { label: t('Desks at work'), className: 'text-muted-foreground' },
  people: { label: t('Waits for you'), className: 'text-orange-700 dark:text-orange-300' },
};

const WATCH_EVENT: Record<VickiWatch['event'], string> = {
  table_changed: t('Table changes'),
  work_item_changed: t('Work Item changes'),
  approval_requested: t('Approvals'),
  run_failed: t('Failed runs'),
  files_changed: t('File changes'),
  view_changed: t('View changes'),
};

/** Rows shown before "and n more". */
const ROWS = 3;

/**
 * What VICKI keeps at in this chat (docs/features/front-desk.md#driving-a-chat): each open mission with whom it waits on
 * and its next step, and what she watches on the event bus for the chat. People stop a mission or a watch here.
 */
export function ChatMissions({ conversationId }: { conversationId: string }) {
  const queryClient = useQueryClient();
  const [all, setAll] = useState(false);
  const drive = useQuery({ queryKey: qk.vickiDrive(conversationId), queryFn: () => api.frontDesk.drive(conversationId) });
  useRealtimeEnvelope((env) => {
    const data = (env.data ?? {}) as { conversationId?: string; drive?: boolean; metadata?: { event?: string } };
    const mine = data.conversationId === conversationId;
    if (mine && (data.drive || data.metadata?.event === 'vicki_mission')) void queryClient.invalidateQueries({ queryKey: qk.vickiDrive(conversationId) });
  });
  const stop = useApiMutation((id: string) => api.frontDesk.stopMission(id), { invalidate: [qk.vickiDrive(conversationId)] });
  const end = useApiMutation((id: string) => api.frontDesk.endWatch(id), { invalidate: [qk.vickiDrive(conversationId)] });
  const missions = drive.data?.missions ?? [];
  const watches = drive.data?.watches ?? [];
  if (!missions.length && !watches.length) return null;
  const rows = [...missions.map((m) => ({ kind: 'mission' as const, m })), ...watches.map((w) => ({ kind: 'watch' as const, w }))];
  const shown = all ? rows : rows.slice(0, ROWS);
  return (
    <div className="flex flex-col gap-1 rounded-lg border bg-muted/40 px-2.5 py-1.5 text-xs" aria-live="polite">
      {shown.map((row) =>
        row.kind === 'mission' ? (
          <MissionRow key={row.m.id} mission={row.m} onStop={() => stop.mutate(row.m.id)} stopping={stop.isPending && stop.variables === row.m.id} />
        ) : (
          <div key={row.w.id} className="group flex min-w-0 items-center gap-2 py-0.5">
            <Eye className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="shrink-0 font-medium text-foreground/90">{WATCH_EVENT[row.w.event]}</span>
            <span className="min-w-0 flex-1 truncate text-muted-foreground" title={row.w.why}>
              {[row.w.deskName ?? row.w.workspaceName, row.w.tables.join(', '), row.w.why].filter(Boolean).join(' · ')}
            </span>
            <button
              type="button"
              onClick={() => end.mutate(row.w.id)}
              className="shrink-0 cursor-pointer rounded p-0.5 text-muted-foreground opacity-60 hover:bg-background hover:text-foreground group-hover:opacity-100"
              aria-label={t('Stop watching')}
              title={t('Stop watching')}
            >
              <X className="size-3.5" />
            </button>
          </div>
        ),
      )}
      {rows.length > ROWS && (
        <button type="button" onClick={() => setAll(!all)} className="flex cursor-pointer items-center gap-1 self-start text-muted-foreground hover:text-foreground">
          <ChevronDown className={cn('size-3.5 transition-transform', all && 'rotate-180')} />
          {all ? t('Show less') : t('and {n} more', { n: rows.length - ROWS })}
        </button>
      )}
    </div>
  );
}

function MissionRow({ mission, onStop, stopping }: { mission: VickiMission; onStop: () => void; stopping: boolean }) {
  const waiting = WAITING[mission.waitingOn];
  return (
    <div className="group flex min-w-0 items-start gap-2 py-0.5">
      <Target className="mt-px size-3.5 shrink-0 text-primary" />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="min-w-0 truncate font-medium text-foreground/90" title={mission.doneWhen ? `${mission.goal}\n${t('Done when: {x}', { x: mission.doneWhen })}` : mission.goal}>
            {mission.goal}
          </span>
          <span className={cn('shrink-0', waiting.className)}>· {waiting.label}</span>
        </div>
        {mission.nextStep && (
          <div className="truncate text-muted-foreground" title={mission.nextStep}>
            {t('Next: {step}', { step: mission.nextStep })}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onStop}
        disabled={stopping}
        className="shrink-0 cursor-pointer rounded p-0.5 text-muted-foreground opacity-60 hover:bg-background hover:text-foreground group-hover:opacity-100"
        aria-label={t('Stop this')}
        title={t('Stop this: VICKI no longer keeps at it')}
      >
        {stopping ? <Loader2 className="size-3.5 animate-spin" /> : <Square className="size-3.5" />}
      </button>
    </div>
  );
}

const DRIVE_EVENTS = ['vicki_mission', 'vicki_drive', 'vicki_watch', 'vicki_waiting'];

export function isDriveNotice(m: ConversationMessage) {
  return m.senderType === 'system' && DRIVE_EVENTS.includes(String((m.metadata as { event?: string } | undefined)?.event));
}

/**
 * The driver's notices in the thread, short: a mission started or ended, VICKI going on or looking after the desks' work
 * (what Deskworks told her opens on click), what a watch gathered, and a reminder that she waits for people.
 */
export function DriveNotice({ message }: { message: ConversationMessage }) {
  const meta = message.metadata as { event?: string; reason?: string; status?: string } | undefined;
  const [open, setOpen] = useState(false);
  const [head, ...rest] = message.content.split('\n');
  const event = meta?.event;
  const ended = event === 'vicki_mission' && meta?.status !== 'open';
  const icon =
    event === 'vicki_mission' ? (
      ended ? <CheckCircle2 className="size-3.5 text-emerald-600 dark:text-emerald-400" /> : <Target className="size-3.5 text-primary" />
    ) : event === 'vicki_watch' ? (
      <Eye className="size-3.5" />
    ) : (
      <Hourglass className="size-3.5" />
    );
  const label =
    event === 'vicki_mission'
      ? head.replace(/^(🎯|✅|⏹)\s*/, '')
      : event === 'vicki_drive'
        ? meta?.reason === 'check'
          ? t('VICKI looks after the desks’ work')
          : meta?.reason === 'limit'
            ? head
            : t('VICKI goes on')
        : head;
  const details = event === 'vicki_drive' && meta?.reason !== 'limit' ? message.content : rest.join('\n').trim();
  return (
    <div className="flex justify-center">
      <div
        title={formatAbsolute(message.createdAt, true)}
        className={cn(
          'max-w-[90%] rounded-lg px-3 py-1.5 text-xs break-words',
          event === 'vicki_waiting' ? 'bg-orange-50 text-orange-800 dark:bg-orange-950/40 dark:text-orange-200' : 'bg-muted text-muted-foreground',
        )}
      >
        <button
          type="button"
          disabled={!details}
          onClick={() => setOpen(!open)}
          className={cn('flex items-center gap-1.5 text-left', details && 'cursor-pointer hover:text-foreground')}
        >
          {icon}
          <span className={cn(event === 'vicki_mission' && 'font-medium text-foreground/80')}>{label}</span>
          {details && <ChevronDown className={cn('size-3 shrink-0 transition-transform', open && 'rotate-180')} />}
        </button>
        {open && details && <div className="mt-1 border-t pt-1 whitespace-pre-wrap">{details}</div>}
      </div>
    </div>
  );
}
